import { Component, OnDestroy, OnInit, inject, DOCUMENT, effect } from '@angular/core';

import { Platform } from '@angular/cdk/platform';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { CdkConfigService } from '@cdk/services/config.service';
import { CdkNavigationService } from '@cdk/components/navigation/navigation.service';
import { CdkSidebarService } from '@cdk/components/sidebar/sidebar.service';
import { CdkTranslationLoaderService } from '@cdk/services/translation-loader.service';

import { navigation } from 'app/navigation/navigation';
import { locale as navigationEnglish } from 'app/navigation/i18n/en';
import { select, Store } from '@ngrx/store';
import { getMercureState, State } from 'app/store/reducers';
import { SetScreen } from 'app/store';
import { modulesConfig } from '../modules/modules-config';
import { LoginService } from './main/auth/login/login.service';
import * as AssinaturaStore from 'app/store';
import { UpdateData } from '../@cdk/ngrx-normalizr';
import { ComponenteDigital, Documento, Tarefa } from '../@cdk/models';
import { documento as documentoSchema, tarefa as tarefaSchema } from '../@cdk/normalizr';
import { PrimeNG } from 'primeng/config';
import {provideLuxonDateAdapter} from '@angular/material-luxon-adapter';
import {MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition} from '@angular/material/snack-bar';
import {CacheModelService} from '../@cdk/services/cache.service';
import {AssistenteStore, AssistenteStoreType} from './main/apps/assistente-movimentar/assistente.store';
import {DocumentoStore, DocumentoStoreType} from './main/apps/documento/documento.store';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
    providers: [
        provideLuxonDateAdapter()
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    private document = inject(DOCUMENT);
    private _cdkConfigService = inject(CdkConfigService);
    private _cdkNavigationService = inject(CdkNavigationService);
    private _cdkSidebarService = inject(CdkSidebarService);
    private _cdkTranslationLoaderService = inject(CdkTranslationLoaderService);
    private _translateService = inject(TranslateService);
    private _platform = inject(Platform);
    private _store = inject<Store<State>>(Store);
    private _loginService = inject(LoginService);
    private _cacheComponenteDigitalModelService = inject<CacheModelService<ComponenteDigital>>(CacheModelService);
    private snackBar = inject(MatSnackBar);
    private primeng = inject(PrimeNG);
    private _router = inject(Router);

    readonly assistenteStore: AssistenteStoreType = inject(AssistenteStore);
    readonly documentoStore: DocumentoStoreType = inject(DocumentoStore);

    cdkConfig: any;
    navigation: any;
    resize$: any;
    assinaturaErrors$: Observable<any>;
    horizontalPosition: MatSnackBarHorizontalPosition = 'center';
    verticalPosition: MatSnackBarVerticalPosition = 'top';
    isProtocoloRoute: boolean = false;

    // Private
    private _assinandoDocumentosId$: Observable<number[]>;
    private _javaWebStartOK = false;
    private _assinaturaInterval = null;
    private _unsubscribeAll: Subject<any>;
    private firstNavigation = true;


    constructor() {
        // Get default navigation
        this.navigation = navigation;

        modulesConfig.forEach((module) => {
            if (module.mainMenu) {
                module.mainMenu.forEach((i) => {
                    this.navigation[0].children.forEach((n) => {
                        if (n.id === i.id) {
                            i.entries.forEach((j) => {
                                n.children.push(j);
                            });

                            n.role = [].concat(i['role'] ?? [], n.role);
                        }
                    });
                    if (i.id === 'modulos') {
                        i.entries.forEach((j) => {
                            this.navigation[1].children.push(j);
                        });
                    }
                });
            }
        });

        // Controla visibilidade do Formulário IA (Pesquisa Inteligente).
        // Busca a thunk via moduleState do módulo IA (quando carregado no build);
        // não há import estático do Host para o módulo de extensão. A thunk é
        // invocada aqui, dentro do construtor (contexto de injeção), retornando
        // um Signal que é lido dentro do effect() — necessário porque o mapa de
        // beta-features carrega de forma assíncrona, depois do boot do menu
        // (aiBetaFeaturesResolver dispara na navegação para a rota 'apps').
        const iaBetaState = modulesConfig
            .find((m: any) => m.moduleState?.['ia.beta.state'])
            ?.moduleState?.['ia.beta.state'];

        if (iaBetaState) {
            const navState = iaBetaState('pesquisa-inteligente')();

            effect(() => {
                const { allowed } = navState();

                this._cdkNavigationService.updateNavigationItem('formulario-ia', {
                    hidden: !allowed,
                });
            });
        }

        // Register the navigation to the service
        this._cdkNavigationService.register('main', this.navigation);

        // Set the main navigation as our current navigation
        this._cdkNavigationService.setCurrentNavigation('main');

        // Add languages
        this._translateService.addLangs(['pt']);

        // Set the default language
        this._translateService.setDefaultLang('pt');

        // Set the navigation translations
        this._cdkTranslationLoaderService.loadTranslations(navigationEnglish);

        // Use a language
        this._translateService.use('pt');

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix Start
         * ----------------------------------------------------------------------------------------------------
         */

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         */

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.

        setTimeout(() => {
            this._translateService.setDefaultLang('en');
        });

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix End
         * ----------------------------------------------------------------------------------------------------
         */

        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.primeng.zIndex = {
            modal: 110000,    // dialog, sidebar
            overlay: 100001,  // dropdown, overlaypanel
            menu: 100001,     // overlay menus
            tooltip: 110000   // tooltip
        };
        // Subscribe to config changes
        this._cdkConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {

                this.cdkConfig = config;

                // Boxed
                if (this.cdkConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                } else {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                // tslint:disable-next-line:prefer-for-of
                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (let i = 0; i < this.document.body.classList.length; i++) {
                    const className = this.document.body.classList[i];

                    if (className.startsWith('theme-')) {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.cdkConfig.colorTheme);
            });
        this._assinandoDocumentosId$ = this._store.pipe(select(AssinaturaStore.getDocumentosAssinandoIds));
        this.assinaturaErrors$ = this._store.pipe(select(AssinaturaStore.getAssinaturaErrors));
        this.resize$ = fromEvent(window, 'resize')
            .pipe(
                debounceTime(200),
                map(() => window.innerWidth),
                distinctUntilChanged(),
                startWith(window.innerWidth),
                tap((width) => {
                    let payload = 'mobile';
                    if (width > 425 && width <= 1024) {
                        payload = 'tablet';
                    }
                    if (width > 1024) {
                        payload = 'desktop';
                    }
                    this._store.dispatch(new SetScreen(
                        payload
                    ));
                }),
            );
        this.resize$.pipe(takeUntil(this._unsubscribeAll)).subscribe();

        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationEnd) => {
                this.isProtocoloRoute = event.url === '/protocolo';
                if (this.firstNavigation) {
                    this.firstNavigation = false;
                    return;
                }

                const main = document.getElementById('main-content');
                main?.focus();
            });

        if (this._loginService.getUserProfile() && !this._loginService.isExpired()) {
            this._loginService.startCountdown();

            // Aplica tema do usuário após F5 (profile já está no localStorage)
            const aparenciaConfig = this._loginService.getConfiguracao('aparencia');
            if (aparenciaConfig && !aparenciaConfig.default && aparenciaConfig.valor) {
                const config = aparenciaConfig.valor;
                this._cdkConfigService.config = config;
            }
        }
        this._store.pipe(
            select(getMercureState),
            takeUntil(this._unsubscribeAll)
        ).subscribe((message) => {
            if (message && message.type === 'assinatura') {
                switch (message.content.action) {
                    case 'assinatura_iniciada':
                        this._javaWebStartOK = true;
                        break;
                    case 'assinatura_cancelada':
                        this._javaWebStartOK = false;
                        this._store.dispatch(new AssinaturaStore.AssinaDocumentoFailed(message.content.documentoId));
                        break;
                    case 'assinatura_erro':
                        this._javaWebStartOK = false;
                        this._store.dispatch(new AssinaturaStore.AssinaDocumentoFailed(message.content.documentoId));
                        break;
                    case 'assinatura_finalizada':
                        this._javaWebStartOK = false;
                        this._store.dispatch(new AssinaturaStore.AssinaDocumentoSuccess(message.content.documentoId));
                        this._store.dispatch(new UpdateData<Documento>({
                            id: message.content.documentoId,
                            schema: documentoSchema,
                            changes: { assinado: true }
                        }));
                        if (message.content.documentoId === this.assistenteStore.documentoId()) {
                            this.assistenteStore.getDocumento(this.assistenteStore.componenteDigital().id);
                        }
                        break;

                    case 'SIGN_DELETE':
                        this._store.dispatch(new UpdateData<Documento>({
                            id: message.content?.documentoId,
                            schema: documentoSchema,
                            changes: {assinado: false}
                        }));
                        if (this.assistenteStore.minutasEAnexos().includes(message.content.documentoId)) {
                            // Documento com assinatura removida se encontra no signalStore de assistente de movimentação
                            if (message.content.documentoId === this.assistenteStore.documentoId()) {
                                // Documento com assinatura removida está sendo editado atualmente, recarregar seu conteúdo
                                this.assistenteStore.getDocumento(this.assistenteStore.componenteDigital().id);
                            }
                            this.assistenteStore.removeAssinatura(message.content.documentoId);
                        }
                        if (this.documentoStore.minutasEAnexos().includes(message.content.documentoId)) {
                            // Documento com assinatura removida encontra-se no store do editor
                            this.documentoStore.removeAssinatura(message.content.documentoId);
                        }
                        break;

                    case 'SIGN_ERROR':
                        this._store.dispatch(new AssinaturaStore.AssinaDocumentoFailed({
                            documentoId: message.content.documentoId,
                            error: message.content.message || 'Erro desconhecido' // Incluindo a mensagem de erro recebida
                        }));
                        this._store.dispatch(new UpdateData<Documento>({
                            id: message.content.documentoId,
                            schema: documentoSchema,
                            changes: {assinado: false}
                        }));
	            	    break;

                    case 'SIGN_FINISHED':
                        this._store.dispatch(new AssinaturaStore.AssinaDocumentoSuccess(message.content.documentoId));
                        if (message?.content?.message) {
                            this._store.dispatch(new AssinaturaStore.AssinaDocumentoSuccessWithMessage({
                                id: message.content.documentoId,
                                message: message.content.message
                            }));
                        }
                        this._store.dispatch(new UpdateData<Documento>({
                            id: message.content.documentoId,
                            schema: documentoSchema,
                            changes: { assinado: true }
                        }));
                        if (this.assistenteStore.minutasEAnexos().includes(message.content.documentoId)) {
                            // Documento assinado se encontra no signalStore de assistente de movimentação
                            if (message.content.documentoId === this.assistenteStore.documentoId()) {
                                // Documento assinado está sendo editado atualmente, recarregar seu conteúdo
                                this.assistenteStore.getDocumento(this.assistenteStore.componenteDigital().id);
                            }
                            if (this.assistenteStore.minutas().findIndex(documento => documento.id === message.content.documentoId) !== -1) {
                                // É uma minuta, atualizá-la com informações de assinatura
                                this.assistenteStore.minutaAssinada(message.content.documentoId);
                            }
                            if (this.assistenteStore.anexos().findIndex(documento => documento.id === message.content.documentoId) !== -1) {
                                // É uma minuta, atualizá-la com informações de assinatura
                                this.assistenteStore.anexoAssinado(message.content.documentoId);
                            }
                        }


                        if (this.documentoStore.minutasEAnexos().includes(message.content.documentoId)) {
                            // Documento assinado encontra-se no store do editor
                            if (this.documentoStore.minutas().findIndex(documento => documento.id === message.content.documentoId) !== -1) {
                                // É uma minuta, atualizá-la com informações de assinatura
                                this.documentoStore.minutaAssinada(message.content.documentoId);
                            }
                            if (this.documentoStore.anexos().findIndex(documento => documento.id === message.content.documentoId) !== -1) {
                                // É uma minuta, atualizá-la com informações de assinatura
                                this.documentoStore.anexoAssinado(message.content.documentoId);
                            }
                        }
                        break;
                }
            }

            if (message && message.type === 'tarefa') {
                switch (message.content.action) {
                    case 'TAREFA_UPDATE_SUCCESS':
                        this._store.dispatch(new UpdateData<Tarefa>({
                            id: message.content.tarefaId,
                            schema: tarefaSchema,
                            changes: structuredClone(message?.content?.changes)
                        }));

                        break;

                    case 'TAREFA_UPDATE_ERROR':
                        this._store.dispatch(new UpdateData<Tarefa>({
                            id: message.content.tarefaId,
                            schema: tarefaSchema,
                            changes: structuredClone(message?.content?.changes)
                        }));

                        break;
                }
            }

        });

        this._assinandoDocumentosId$.pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe((assinandoDocumentosId) => {
            if (assinandoDocumentosId.length > 0) {
                if (this._javaWebStartOK) {
                    if (this._assinaturaInterval) {
                        clearInterval(this._assinaturaInterval);
                    }
                    this._assinaturaInterval = setInterval(() => {
                        // monitoramento do java
                        if (!this._javaWebStartOK && (assinandoDocumentosId.length > 0)) {
                            assinandoDocumentosId.forEach(
                                documentoId => this._store.dispatch(new AssinaturaStore.AssinaDocumentoFailed(documentoId))
                            );
                        }
                    }, 30000);
                }
            } else {
                this._store.dispatch(new AssinaturaStore.LimpaAssinandoAnexos());
                clearInterval(this._assinaturaInterval);
            }
        });

        this.assinaturaErrors$.pipe(
            takeUntil(this._unsubscribeAll)
        ).subscribe((err) => {
            if (err && err.status && (err.status === 422 || err.status === 401)) {
                const error = (err.error.message || err.statusText || 'Erro desconhecido!').replace('Unknown Error', 'Erro Desconhecido!');
                this.snackBar.open(error, 'Fechar', {
                    horizontalPosition: this.horizontalPosition,
                    verticalPosition: this.verticalPosition,
                    panelClass: ['danger-snackbar'],
                    duration: 30000
                });
            }
        });

        this._cacheComponenteDigitalModelService.initialize(this._loginService.getUserProfile()?.username, ComponenteDigital);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(true);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._cdkSidebarService.getSidebar(key).toggleOpen();
    }
}
